// src/pages/Marketplace/surge-pricing/AuditLogPage.tsx
import React, { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import { acquireApiToken } from '@/auth/acquireApiToken';
import { Env, ENV_COLORS, ErrorBanner, Spinner, getAdminApiUrl } from '@/pages/Admin/shared';
import { useSurgeAuditLog } from '@/pages/Marketplace/shared';
import type { AuditLogEntry, AuditLogFilters } from '@/pages/Marketplace/shared';

const LS_KEY_ENV = 'marketplace.env';
const ACTION_TYPES = ['market.create', 'market.update', 'market.delete',
  'event.create', 'event.update', 'event.delete',
  'business.override', 'business.clear_override'] as const;

export default function AuditLogPage(): React.ReactElement {
  const { instance, accounts } = useMsal();
  const [env, setEnv] = useState<Env>(() => (localStorage.getItem(LS_KEY_ENV) as Env) || 'dev');
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [params, setParams] = useSearchParams();
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});

  useEffect(() => { localStorage.setItem(LS_KEY_ENV, env); }, [env]);
  useEffect(() => {
    if (accounts.length === 0) return;
    acquireApiToken(instance, accounts[0]).then((accessToken) => ({ accessToken }))
      .then((r) => setAccessToken(r.accessToken))
      .catch(() => setAccessToken(null));
  }, [instance, accounts]);

  const filters: AuditLogFilters = useMemo(() => ({
    adminEmail: params.get('adminEmail') ?? undefined,
    from: params.get('from') ?? undefined,
    to: params.get('to') ?? undefined,
    actions: params.getAll('action').length ? params.getAll('action') : undefined,
  }), [params]);

  const apiUrl = getAdminApiUrl(env);
  const { items, cursor, loading, error, loadMore } = useSurgeAuditLog(apiUrl, accessToken, filters);

  const updateParam = (key: string, value: string | string[] | null) => {
    const next = new URLSearchParams(params);
    next.delete(key);
    if (Array.isArray(value)) value.forEach((v) => next.append(key, v));
    else if (value) next.set(key, value);
    setParams(next, { replace: true });
  };

  const envColors = ENV_COLORS[env];

  return (
    <div className="p-6 text-gray-200 min-h-[calc(100vh-72px)]">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div>
          <Link to="/surge-pricing" className="text-xs text-gray-400 hover:text-white">← Surge Pricing</Link>
          <h1 className="text-xl font-semibold text-white mt-1">Audit Log</h1>
        </div>
        <select
          value={env}
          onChange={(e) => setEnv(e.target.value as Env)}
          className={`text-xs px-2 py-1 rounded border ${envColors.pill}`}
        >
          <option value="dev">dev</option>
          <option value="staging">staging</option>
          <option value="prod">prod</option>
        </select>
      </div>

      {/* Filters */}
      <div className="grid grid-cols-4 gap-3 mb-4">
        <label className="block">
          <span className="text-xs text-gray-400">Admin email</span>
          <input
            className="w-full bg-gray-900 border border-gray-700 rounded px-2 py-1 text-sm mt-1"
            defaultValue={filters.adminEmail ?? ''}
            onBlur={(e) => updateParam('adminEmail', e.target.value || null)}
          />
        </label>
        <label className="block">
          <span className="text-xs text-gray-400">From</span>
          <input
            type="date"
            className="w-full bg-gray-900 border border-gray-700 rounded px-2 py-1 text-sm mt-1"
            value={filters.from ?? ''}
            onChange={(e) => updateParam('from', e.target.value || null)}
          />
        </label>
        <label className="block">
          <span className="text-xs text-gray-400">To</span>
          <input
            type="date"
            className="w-full bg-gray-900 border border-gray-700 rounded px-2 py-1 text-sm mt-1"
            value={filters.to ?? ''}
            onChange={(e) => updateParam('to', e.target.value || null)}
          />
        </label>
        <label className="block">
          <span className="text-xs text-gray-400">Action types</span>
          <select
            multiple
            className="w-full bg-gray-900 border border-gray-700 rounded px-2 py-1 text-sm mt-1 h-20"
            value={filters.actions ?? []}
            onChange={(e) => updateParam('action', Array.from(e.target.selectedOptions).map((o) => o.value))}
          >
            {ACTION_TYPES.map((a) => <option key={a} value={a}>{a}</option>)}
          </select>
        </label>
      </div>

      {error && <ErrorBanner message={error} />}

      <div className="border border-gray-800 rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-900 text-gray-400 text-xs uppercase">
            <tr>
              <th className="text-left px-3 py-2">When</th>
              <th className="text-left px-3 py-2">Who</th>
              <th className="text-left px-3 py-2">Action</th>
              <th className="text-left px-3 py-2">Target</th>
              <th className="text-right px-3 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {items.map((e: AuditLogEntry) => (
              <React.Fragment key={e.id}>
                <tr className="border-t border-gray-800">
                  <td className="px-3 py-2 text-gray-400 whitespace-nowrap">
                    {new Date(e.timestamp).toLocaleString()}
                  </td>
                  <td className="px-3 py-2">{e.changed_by_email}</td>
                  <td className="px-3 py-2">{e.action}</td>
                  <td className="px-3 py-2 text-gray-400">{e.target_label ?? `${e.target_type}/${e.target_id}`}</td>
                  <td className="px-3 py-2 text-right">
                    <button
                      onClick={() => setExpanded((x) => ({ ...x, [e.id]: !x[e.id] }))}
                      className="text-xs text-violet-300 hover:text-violet-200"
                    >
                      {expanded[e.id] ? 'Hide' : 'Show'} diff
                    </button>
                  </td>
                </tr>
                {expanded[e.id] && (
                  <tr className="border-t border-gray-800 bg-gray-950">
                    <td colSpan={5} className="px-3 py-3">
                      <div className="grid grid-cols-2 gap-3 text-xs">
                        <pre className="border border-gray-800 rounded p-2 overflow-x-auto">
<span className="text-red-300">before</span>{'\n'}{JSON.stringify(e.before, null, 2)}
                        </pre>
                        <pre className="border border-gray-800 rounded p-2 overflow-x-auto">
<span className="text-emerald-300">after</span>{'\n'}{JSON.stringify(e.after, null, 2)}
                        </pre>
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
        {loading && <div className="p-3"><Spinner size="sm" /></div>}
        {cursor && !loading && (
          <button onClick={loadMore} className="w-full text-center text-xs text-gray-400 hover:text-white py-2 border-t border-gray-800">
            Load more
          </button>
        )}
      </div>
    </div>
  );
}
